import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted } from "vue";
import * as Sentry from "@sentry/browser";
import { useViewAnalytics } from "@/composition/useViewAnalytics";
import {
  ApiService,
  LogService,
  RouterService,
  SnackbarService,
} from "@/services";
import { SearchRouteTypes, VignetteRoutes } from "@/types";
import { useSessionStore } from "@/pinia/session";
import { useAppStore } from "@/pinia/app";
import { useRouter } from "vue-router/composables";
import ServerErrorComponent from "@/components/ServerError.vue";
import HeadMetadata from "@/components/HeadMetadata.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'LoadingPage',
  setup(__props) {

const router = useRouter();

const appStore = useAppStore();
const sessionStore = useSessionStore();

const pageName = ref("Loading");
const { pageTitle } = useViewAnalytics(pageName);
const showLoadingText = ref(false);
const display500 = ref(false);

const insertSearchSuggestions = () => {
  const searchSuggestions = appStore.searchSuggestions;
  for (const k in searchSuggestions) {
    const key: SearchRouteTypes = k as SearchRouteTypes;
    const searchRoute =
      VignetteRoutes[`Search${key}` as keyof typeof VignetteRoutes];
    const route = router.resolve({ name: searchRoute }).route;
    if (route && route.meta && searchSuggestions[key]) {
      route.meta.searchHint = searchSuggestions[key];
    }
  }
};

const timer = ref<number | null>(null);

onMounted(async () => {
  // we want to also do a timeout in the event that a halting error occurs and doesn't get sent/caught.
  timer.value = setTimeout(() => {
    LogService.warn(
      "Loading page took more than 10 seconds to load. More details should be available."
    );
  }, 10000);
  setTimeout(() => {
    showLoadingText.value = true;
  }, 2000);
  // start tracking how long queries take with details for each query
  try {
    performance.mark("start-total");
    performance.mark("start-appConfig");
    await ApiService.getAppConfig();
    performance.mark("end-appConfig");
    insertSearchSuggestions();
    performance.mark("start-session");
    await ApiService.getSession();
    performance.mark("end-session");

    if (sessionStore.hasVignetteAccess) {
      performance.mark("start-briefcase");
      ApiService.refreshBriefcase();
      performance.mark("end-briefcase");
    }
    performance.mark("end-total");

    loadComplete();
  } catch (err) {
    // Check if the error is a 500 error
    const error = err as Error;
    const status = (err as any)?.response?.status;
    LogService.error("LoadingPage initialization failed", error, { status });
    if (status && Number(status) >= 500) {
      // Set loading to false
      appStore.loading = false;
      // Display server error component
      display500.value = true;
    } else {
      SnackbarService.displaySnackbar(
        "An error occurred, please try again.",
        "error"
      );
    }
  }
});

function loadComplete(): void {
  LogService.debug("Vue app finished loading all dependencies.");
  appStore.loading = false;

  // Customize Sentry with user info, if available
  customizeSentry();
  analyzePerformance();

  const redirectPath = appStore.redirectPath;

  // Clear redirect path
  appStore.redirectPath = "";

  timer.value && clearTimeout(timer.value);
  /** IMPORTANT NOTE: redirectPath never empty on app load, it's always set to "/" */
  // "!redirectPath" returns true if falsy, it is JS/TS shorthand to check falsy values (null,undefined,"",0)
  if (sessionStore.hasLapsedSubscription) {
    // Only for lapsed subscriptions, automatically redirect from Welcome to My Account page after login / app load.
    if (!redirectPath || redirectPath === "/") {
      RouterService.navigateByName(router, VignetteRoutes.MyAccount);
    } else {
      RouterService.navigateByPath(router, redirectPath);
    }
  } else {
    if (!redirectPath) {
      RouterService.navigateToHome(router);
    } else {
      RouterService.navigateByPath(router, redirectPath);
    }
  }
}

/**
 * Analyzes the performance marks and logs a warning if the total time is greater than 10 seconds.
 * Creates an object to store the marks and their results (in seconds).
 */
function analyzePerformance(): void {
  const entries = performance.getEntriesByType("mark");
  const marks = entries.map((entry) => entry.name);
  const marksObject: Record<string, number> = {
    appConfig: 0,
    session: 0,
    briefcase: 0,
    total: 0,
  };
  for (const key in marksObject) {
    const startMarkName = `start-${key}`;
    const endMarkName = `end-${key}`;
    if (marks.includes(startMarkName) && marks.includes(endMarkName)) {
      const result =
        performance.getEntriesByName(endMarkName)[0].startTime -
        performance.getEntriesByName(startMarkName)[0].startTime;
      marksObject[key] = +(result / 1000).toFixed(2);
    }
  }
  const message = `FETCH TIME: Loading took ${marksObject.total.toFixed(
    2
  )} seconds to execute. Breakdown: ${JSON.stringify(marksObject)}`;
  if (marksObject.total > 10) {
    LogService.warn(message);
  } else {
    LogService.info(message);
  }
}

function customizeSentry(): void {
  if (LogService.isSentryLoaded) {
    const isLoggedIn = sessionStore.isLoggedIn;
    const hasVignetteAccess = sessionStore.hasVignetteAccess;
    const user = sessionStore.user;

    Sentry.configureScope(function (scope) {
      if (user) {
        scope.setUser({
          email: user.email,
          username: user.username,
        });
        scope.setTag("userType", user.userType);
      }
      scope.setTag("isLoggedIn", String(isLoggedIn));
      scope.setTag("hasVignetteAccess", String(hasVignetteAccess));
    });
  }
}

return { __sfc: true,router, appStore, sessionStore, pageName, pageTitle, showLoadingText, display500, insertSearchSuggestions, timer, loadComplete, analyzePerformance, customizeSentry, ServerErrorComponent, HeadMetadata }
}

})